// Settings → Connectors — external systems that connect INTO the WMS.
// First (and so far only) connector: the MCP endpoint Claude connects to. The page is the
// human-facing side of backend/mcp_server.py: how to connect, whether the service is up, and
// the full tool-call audit log (read from the connector's own SQLite state via main.py).
import { useCallback, useEffect, useState } from 'react';
import { API, authFetch } from './api';
import RestApiPanel from './RestApiPanel';

const CARD = 'bg-[#1a1d27] rounded-xl border border-gray-800 px-5 py-4';

export default function ConnectorsTab() {
  const [sub, setSub] = useState<'mcp' | 'rest' | 'claude_api' | 'mail' | 'google'>('mcp');
  const TABS = [
    { key: 'mcp', label: 'MCP (Claude)' },
    { key: 'rest', label: 'REST API (scripts)' },
    { key: 'claude_api', label: 'Claude API (Label Doctor)' },
    { key: 'mail', label: 'Mail (Warehouse@)' },
    { key: 'google', label: 'Google Workspace (Gmail)' },
  ] as const;
  return (
    <div>
      <div className="flex gap-1 mb-6 border-b border-gray-800 pb-3">
        {TABS.map(t => (
          <button key={t.key} onClick={() => setSub(t.key)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${sub === t.key ? 'bg-gray-700 text-white' : 'text-gray-500 hover:text-white'}`}>
            {t.label}
          </button>
        ))}
      </div>
      {sub === 'mcp' && <McpConnectorPanel />}
      {sub === 'rest' && <RestApiPanel />}
      {sub === 'claude_api' && <ClaudeApiPanel />}
      {sub === 'mail' && <MailPanel />}
      {sub === 'google' && <GoogleWorkspacePanel />}
    </div>
  );
}

// ── Google Workspace (Gmail API) — mails the WMS sends show in the mailbox's Sent ──
// Fernando (2026-10-13/15): internal mails must show in warehouse@'s Sent, customer mails in the
// customer-service mailbox's Sent. A Google service account with domain-wide delegation; its JSON key
// is READ FROM THE FILE here (never typed, never in the clipboard), checked by the server (a token,
// the "Send mail as" list, one test mail that must land in Sent) and only then saved, sealed. The key
// never comes back. backend/gmail.py, docs/GMAIL.md.
const DEFAULT_MAILBOXES = ['warehouse@motleydenim.com', 'customerservice@motleydenim.com', 'asiakaspalvelu@bigoliver.fi'];

type GAlias = { email: string; name: string; primary: boolean; default: boolean; status: string; reply_to: string; has_signature: boolean; signature_html?: string };

function GoogleWorkspacePanel() {
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [keyObj, setKeyObj] = useState<any>(null);
  const [keyNote, setKeyNote] = useState('');
  const [mailboxes, setMailboxes] = useState<string>(DEFAULT_MAILBOXES.join('\n'));
  const [warehouse, setWarehouse] = useState(DEFAULT_MAILBOXES[0]);
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState('');
  const [msg, setMsg] = useState('');
  const [aliases, setAliases] = useState<Record<string, GAlias[]>>({});
  const [open, setOpen] = useState<string>('');
  const [preview, setPreview] = useState<string>('');

  const load = useCallback(() => {
    authFetch(`${API}/google-workspace/status`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`); return r.json(); })
      .then(d => {
        setInfo(d); setError('');
        if (d.configured) {
          setMailboxes((d.mailboxes || []).join('\n')); setWarehouse(d.warehouse_mailbox || DEFAULT_MAILBOXES[0]);
        }
      })
      .catch(e => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const readKey = (f: File | undefined) => {
    setKeyObj(null); setKeyNote(''); setMsg('');
    if (!f) return;
    const r = new FileReader();
    r.onload = () => {
      try {
        const j = JSON.parse(String(r.result || ''));
        if (j.type !== 'service_account' || !j.private_key || !j.client_email) { setKeyNote('✗ Not a service-account key file.'); return; }
        setKeyObj(j);
        setKeyNote(`✓ Key file read: ${j.client_email} · key …${String(j.private_key_id || '').slice(-6)} · Client ID ${j.client_id || '?'}`);
      } catch { setKeyNote('✗ The file is not JSON.'); }
    };
    r.readAsText(f);
  };

  const list = () => mailboxes.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
  // the warehouse pick always names a mailbox on the edited list (else Save would be refused)
  useEffect(() => {
    const l = mailboxes.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean);
    if (l.length && !l.includes(warehouse.trim().toLowerCase())) setWarehouse(l[0]);
  }, [mailboxes, warehouse]);

  const save = async () => {
    setBusy('save'); setMsg('');
    const res = await authFetch(`${API}/google-workspace/key`, {
      method: 'PUT', body: JSON.stringify({
        service_account: keyObj || undefined, keep_key: !keyObj && !!info?.configured,
        mailboxes: list(), warehouse_mailbox: warehouse.trim().toLowerCase(), test_to: testTo.trim() }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy('');
    if (!res.ok) { setMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    setKeyObj(null); setKeyNote('');
    setAliases(a => ({ ...a, [d.mailbox]: d.aliases || [] }));
    setMsg(`✓ Signed in as ${d.mailbox}, sent a test mail to ${d.verified_to} and found it in ${d.mailbox}'s Sent. Saved.`);
    load();
  };
  const check = async (mb: string) => {
    setBusy(`check:${mb}`); setMsg('');
    const res = await authFetch(`${API}/google-workspace/check`, { method: 'POST', body: JSON.stringify({ mailbox: mb }) });
    const d = await res.json().catch(() => ({}));
    setBusy('');
    if (!res.ok) { setMsg(d.detail || `Check failed (HTTP ${res.status})`); return; }
    setAliases(a => ({ ...a, [mb]: d.aliases || [] })); setOpen(mb);
    setMsg(d.ok ? `✓ ${mb}: signed in, ${(d.aliases || []).length} "Send mail as" address(es).` : `${mb}: ${d.error}`);
    load();
  };
  const test = async (mb: string) => {
    setBusy(`test:${mb}`); setMsg('');
    const res = await authFetch(`${API}/google-workspace/test`, { method: 'POST', body: JSON.stringify({ mailbox: mb }) });
    const d = await res.json().catch(() => ({}));
    setBusy('');
    if (!res.ok) { setMsg(d.detail || `Test failed (HTTP ${res.status})`); return; }
    setMsg(`✓ Test mail sent as ${d.mailbox} to ${d.to}${d.in_sent ? ` — it is in ${d.mailbox}'s Sent` : ' — the Sent check did not confirm it yet'}.`);
    load();
  };
  const toggleInternal = async (on: boolean) => {
    setBusy('internal'); setMsg('');
    const res = await authFetch(`${API}/google-workspace/internal`, { method: 'PUT', body: JSON.stringify({ on }) });
    const d = await res.json().catch(() => ({}));
    setBusy('');
    if (!res.ok) { setMsg(d.detail || `Could not change (HTTP ${res.status})`); return; }
    setMsg(on ? '✓ Internal notifications now go through Gmail (they show in the warehouse mailbox\'s Sent).' : '✓ Internal notifications go through the relay again.');
    load();
  };
  const remove = async () => {
    if (!window.confirm('Remove the Google Workspace connection? Internal notifications go back to the relay. (To revoke it at Google too: delete the domain-wide delegation row in the Admin console, then the key in the Cloud console.)')) return;
    const res = await authFetch(`${API}/google-workspace/key`, { method: 'DELETE' });
    if (!res.ok) { setMsg(`Could not remove (HTTP ${res.status})`); return; }
    setMsg('Connection removed.'); load();
  };

  const F = 'px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';
  const fmt = (s?: string) => (s ? new Date(s + (s.endsWith('Z') ? '' : 'Z')).toLocaleString() : '');
  return (
    <div className="space-y-5 max-w-4xl">
      <p className="text-gray-500 text-sm">
        Mails the WMS sends through the <span className="text-gray-300">Gmail API</span> show in the sending mailbox's
        <span className="text-gray-300"> Sent</span> folder — internal mails in the warehouse mailbox, customer mails (later) in the
        customer-service mailbox. The connection is a Google service account with domain-wide delegation; the WMS only ever acts
        as the mailboxes listed here. The key file comes from KeePass — choose the file, never paste it.
      </p>
      <div className={CARD}>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Connection</div>
        {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
        {info && !info.table_ready && <div className="text-xs text-amber-400 mb-2">● The send log table is missing (migration 088) — nothing can be saved yet.</div>}
        {info && (
          <div className="text-sm mb-3">
            {info.configured
              ? <span className="text-green-400">● {info.client_email} · key …{info.key_tail}
                  <span className="text-gray-500"> — saved by {info.set_by}{info.verified_at ? `, test mail ${fmt(info.verified_at)} to ${info.verified_to} found in Sent` : ''}</span></span>
              : <span className="text-amber-400">● not connected — internal notifications use the relay (Mail tab)</span>}
          </div>
        )}
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-xs text-gray-500 md:col-span-2">Key file (.json from KeePass){info?.configured ? ' — leave empty to keep the saved key' : ''}
            <input type="file" accept=".json,application/json" onChange={e => readKey(e.target.files?.[0])}
              className="block w-full mt-1 text-sm text-gray-300 file:mr-3 file:px-3 file:py-1.5 file:rounded file:border-0 file:bg-gray-700 file:text-white" />
            {keyNote && <span className={`block mt-1 ${keyNote.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{keyNote}</span>}
          </label>
          <label className="text-xs text-gray-500">Mailboxes the WMS may act as (one per line)
            <textarea value={mailboxes} onChange={e => setMailboxes(e.target.value)} rows={4} className={`${F} w-full mt-1 font-mono`} /></label>
          <div className="space-y-3">
            <label className="text-xs text-gray-500 block">Warehouse mailbox (internal mails + the test mail)
              <select value={warehouse} onChange={e => setWarehouse(e.target.value)} className={`${F} w-full mt-1`}>
                {list().map(m => <option key={m} value={m}>{m}</option>)}
              </select></label>
            <label className="text-xs text-gray-500 block">Send the test mail to (empty = your WMS user's e-mail)
              <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@motleydenim.com" className={`${F} w-full mt-1`} /></label>
          </div>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={save} disabled={!!busy || (!keyObj && !info?.configured) || !info?.table_ready}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg">
            {busy === 'save' ? 'Signing in, sending, checking Sent…' : 'Verify & save'}
          </button>
          {info?.configured && (
            <button onClick={remove} disabled={!!busy} className="px-3 py-2 bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 text-sm rounded-lg">Remove</button>)}
        </div>
        {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
      </div>

      {info?.configured && (
        <div className={CARD}>
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Internal notifications</div>
          <label className="flex items-center gap-2 text-sm text-gray-300">
            <input type="checkbox" checked={!!info.internal_via_gmail} disabled={!!busy || !info.verified_at}
              onChange={e => toggleInternal(e.target.checked)} />
            Send internal notifications through Gmail as {info.warehouse_mailbox} (they show in its Sent)
          </label>
          <p className="text-xs text-gray-500 mt-1">Off = the relay (Mail tab), as before. If Gmail cannot sign in, a mail goes through the relay instead and its order note says so.</p>
        </div>
      )}

      {info?.configured && (
        <div className={CARD}>
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Mailboxes</div>
          <div className="space-y-2">
            {(info.mailboxes || []).map((mb: string) => {
              const c = (info.checks || {})[mb];
              const pz = (info.paused || {})[mb];
              const al: GAlias[] = aliases[mb] || c?.aliases || [];
              return (
                <div key={mb} className="border border-gray-800 rounded-lg px-3 py-2">
                  <div className="flex items-center gap-3 text-sm">
                    <span className="font-mono text-gray-200">{mb}</span>
                    {mb === info.warehouse_mailbox && <span className="text-[10px] uppercase text-gray-500">warehouse</span>}
                    <span className={c?.ok ? 'text-green-400 text-xs' : 'text-amber-400 text-xs'}>
                      {c ? (c.ok ? `✓ checked ${fmt(c.at)} · ${al.length} send-as` : `✗ ${c.error}`) : 'not checked yet'}</span>
                    {pz && <span className="text-xs text-red-400">paused until {fmt(pz.until)}: {pz.reason}</span>}
                    <span className="flex-1" />
                    <button onClick={() => check(mb)} disabled={!!busy} className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded">{busy === `check:${mb}` ? 'Checking…' : 'Check'}</button>
                    <button onClick={() => test(mb)} disabled={!!busy} className="px-2 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-xs rounded">{busy === `test:${mb}` ? 'Sending…' : 'Send test to me'}</button>
                    {al.length > 0 && <button onClick={() => setOpen(open === mb ? '' : mb)} className="text-xs text-blue-400">{open === mb ? 'hide' : 'aliases'}</button>}
                  </div>
                  {open === mb && al.length > 0 && (
                    <table className="w-full text-xs mt-2">
                      <thead><tr className="text-gray-500 text-left"><th className="py-1">Address</th><th>Display name</th><th>Status</th><th>Signature</th></tr></thead>
                      <tbody>
                        {al.map(a => (
                          <tr key={a.email} className="border-t border-gray-800 text-gray-300">
                            <td className="py-1 font-mono">{a.email}{a.primary ? ' (primary)' : ''}{a.default ? ' · default' : ''}</td>
                            <td>{a.name}</td>
                            <td>{a.status || '—'}</td>
                            <td>{a.signature_html
                              ? <button onClick={() => setPreview(a.signature_html || '')} className="text-blue-400">preview</button>
                              : (a.has_signature ? 'yes (Check to preview)' : '—')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              );
            })}
          </div>
          {preview && (
            <div className="mt-3">
              <div className="flex justify-between text-xs text-gray-500 mb-1"><span>Signature as Gmail has it</span><button onClick={() => setPreview('')} className="text-blue-400">close</button></div>
              {/* sandboxed: no scripts, no forms — the signature's own HTML and images only */}
              <iframe title="signature" sandbox="" srcDoc={preview} className="w-full h-48 bg-white rounded" />
            </div>
          )}
        </div>
      )}

      {info?.recent?.length > 0 && (
        <div className={CARD}>
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Last Gmail sends</div>
          <table className="w-full text-xs">
            <thead><tr className="text-gray-500 text-left"><th className="py-1">When</th><th>Kind</th><th>From</th><th>To</th><th>Subject</th><th>Status</th></tr></thead>
            <tbody>
              {info.recent.map((r: any) => (
                <tr key={r.id} className="border-t border-gray-800 text-gray-300">
                  <td className="py-1 whitespace-nowrap">{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                  <td>{r.purpose}</td>
                  <td className="font-mono">{r.from_address}</td>
                  <td className="font-mono">{(r.to_addresses || []).join(', ')}</td>
                  <td>{r.subject}</td>
                  <td className={r.status === 'sent' ? 'text-green-400' : r.status === 'failed' ? 'text-red-400' : 'text-amber-400'}
                    title={r.error || ''}>{r.status}{r.status === 'sent' ? (r.in_sent ? ' · in Sent' : ' · Sent not confirmed') : ''}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ── Mail (Warehouse@) — the mailbox the WMS sends internal notifications from ──
// Fernando (2026-09-29): notifications from Warehouse@motleydenim.com. SMTP with
// STARTTLS; Google Workspace = smtp.gmail.com:587 + an App password of the mailbox
// (2-Step Verification on → App passwords), or smtp-relay.gmail.com:587 with no
// password once the admin allows the server IP. Saving = one live login + one test
// mail to the address typed, so a wrong password is refused here. The password is
// sealed at rest and never comes back. Rules live in Settings → Notifications.
function MailPanel() {
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [host, setHost] = useState('smtp.gmail.com');
  const [port, setPort] = useState('587');
  const [from, setFrom] = useState('Warehouse@motleydenim.com');
  const [fromName, setFromName] = useState('Motley Denim warehouse');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [testTo, setTestTo] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    authFetch(`${API}/notifications/mail`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`); return r.json(); })
      .then(d => {
        setInfo(d); setError('');
        if (d.configured) {
          setHost(d.host || 'smtp.gmail.com'); setPort(String(d.port || 587));
          setFrom(d.from_address || ''); setFromName(d.from_name || ''); setUsername(d.username || '');
          if (d.verified_to) setTestTo(d.verified_to);
        }
      })
      .catch(e => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setMsg('');
    const res = await authFetch(`${API}/notifications/mail`, {
      method: 'PUT', body: JSON.stringify({
        host: host.trim(), port: Number(port) || 587, from_address: from.trim(), from_name: fromName.trim(),
        username: username.trim() || from.trim(), smtp_password: password, keep_password: !password && !!info?.has_password,
        test_to: testTo.trim() }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    setMsg(`✓ Logged in and sent a test mail to ${d.verified_to} — check that inbox. Saved.`);
    setPassword(''); load();
  };
  const remove = async () => {
    if (!window.confirm('Remove the mailbox? Notifications queue and wait until a mailbox is set up again.')) return;
    const res = await authFetch(`${API}/notifications/mail`, { method: 'DELETE' });
    if (!res.ok) { setMsg(`Could not remove (HTTP ${res.status})`); return; }
    setMsg('Mailbox removed.'); load();
  };
  const F = 'px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500';
  return (
    <div className="space-y-5 max-w-3xl">
      <p className="text-gray-500 text-sm">
        The mailbox the WMS sends <span className="text-gray-300">internal notifications</span> from (rules in
        Settings → Notifications). Nothing goes to customers. For Google Workspace: sign in as the mailbox →
        Google Account → Security → 2-Step Verification on → <span className="text-gray-300">App passwords</span> →
        name it "WMS" → paste the 16 characters here. Saving logs in and sends one test mail to the address you give.
      </p>
      <div className={CARD}>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Mailbox</div>
        {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
        {info && (
          <div className="text-sm mb-3">
            {info.configured
              ? <span className="text-green-400">● {info.from_address} via {info.host}:{info.port}
                  <span className="text-gray-500"> — saved by {info.set_by}{info.verified_at ? `, test mail sent ${new Date(info.verified_at + 'Z').toLocaleString()} to ${info.verified_to}` : ''}</span></span>
              : <span className="text-amber-400">● no mailbox — notifications queue and wait</span>}
          </div>
        )}
        <div className="grid gap-2 md:grid-cols-2">
          <label className="text-xs text-gray-500">SMTP host<input value={host} onChange={e => setHost(e.target.value)} className={`${F} w-full mt-1`} /></label>
          <label className="text-xs text-gray-500">Port<input value={port} onChange={e => setPort(e.target.value)} className={`${F} w-full mt-1`} /></label>
          <label className="text-xs text-gray-500">From address<input value={from} onChange={e => setFrom(e.target.value)} className={`${F} w-full mt-1`} /></label>
          <label className="text-xs text-gray-500">From name<input value={fromName} onChange={e => setFromName(e.target.value)} className={`${F} w-full mt-1`} /></label>
          <label className="text-xs text-gray-500">Login (empty = the from address)<input value={username} onChange={e => setUsername(e.target.value)} className={`${F} w-full mt-1`} /></label>
          <label className="text-xs text-gray-500">Password — App password for smtp.gmail.com; EMPTY for the SMTP relay{info?.has_password ? ' (empty = keep the stored one)' : ''}
            <input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder={host.includes('relay') ? 'none needed for the relay' : 'xxxx xxxx xxxx xxxx'} className={`${F} w-full mt-1 font-mono`} /></label>
          <label className="text-xs text-gray-500 md:col-span-2">Send the test mail to (your own address)
            <input value={testTo} onChange={e => setTestTo(e.target.value)} placeholder="you@motleydenim.com" className={`${F} w-full mt-1`} /></label>
        </div>
        <div className="flex gap-2 mt-3">
          <button onClick={save} disabled={busy || !from.trim() || !testTo.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg">
            {busy ? 'Logging in and sending…' : 'Verify & save'}
          </button>
          {info?.configured && (
            <button onClick={remove} disabled={busy} className="px-3 py-2 bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 text-sm rounded-lg">Remove</button>)}
        </div>
        {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
        <div className="text-gray-500 text-xs mt-3 space-y-1.5 border-t border-gray-800 pt-3">
          <div className="text-gray-400 font-semibold">How this is set up (2026-09-29)</div>
          <p>The WMS sends through the <span className="text-gray-300">Google Workspace SMTP relay</span> (host smtp-relay.gmail.com, port 587,
            no login, no password). Google identifies the WMS by the server's IP address, which is registered in the admin console:
            admin.google.com → Apps → Google Workspace → Gmail → Routing → <span className="text-gray-300">SMTP relay service</span> → rule "WMS server"
            (allowed senders: only addresses in my domains; accept mail only from IP <span className="font-mono text-gray-300">62.238.2.184</span>; TLS required).</p>
          <p><span className="text-gray-300">If the WMS server ever gets a new IP address</span>, that rule must be updated first — sends fail with
            "Invalid credentials for relay" until it is. The WMS always connects over IPv4; the server's IPv6 address is not registered.</p>
          <p>No App password is used on purpose: the Warehouse@ mailbox is shared by the warehouse laptops and tablets, and an App password
            needs 2-Step Verification, which would ask a second step at every one of their regular re-logins. The App-password route
            (host smtp.gmail.com) is still supported by this card if that ever changes.</p>
          <p>Verify &amp; save logs in and sends one test mail to the address above; the rules that decide who gets what are in Settings → Notifications.</p>
        </div>
      </div>
    </div>
  );
}

// ── Claude API (Label Doctor) — the OUTBOUND Claude connection ────────────────
// The MCP tab above is Claude connecting INTO the WMS; this is the WMS calling
// Claude's API itself: the Label Doctor diagnoses failed label buys and leaves
// notes attributed "Claude API". Dormant until a key is saved here.
function ClaudeApiPanel() {
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');
  const [keyInput, setKeyInput] = useState('');
  const [wsInput, setWsInput] = useState('');
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const load = useCallback(() => {
    authFetch(`${API}/claude-api-settings`)
      .then(async r => { if (!r.ok) throw new Error((await r.json()).detail || `HTTP ${r.status}`); return r.json(); })
      .then(d => { setInfo(d); setError(''); })
      .catch(e => setError(e.message));
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async (clear = false) => {
    if (!clear && !keyInput.trim()) return;
    if (clear && !window.confirm('Remove the API key? The Label Doctor stops diagnosing until a new one is entered.')) return;
    setBusy(true); setMsg('');
    const res = await authFetch(`${API}/claude-api-settings`, {
      method: 'PUT', body: JSON.stringify({
        api_key: clear ? '' : keyInput.trim(),
        workspace_id: clear ? '' : wsInput.trim(),
      }),
    });
    const d = await res.json().catch(() => ({}));
    setBusy(false);
    if (!res.ok) { setMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    setMsg(clear ? 'Key removed.' : `✓ Key verified against the Anthropic API and saved (…${d.key_tail}). The doctor picks it up within 5 minutes — no restart.`);
    setKeyInput(''); setWsInput('');
    load();
  };

  const doctor = info?.doctor || {};
  const [modeBusy, setModeBusy] = useState(false);
  const [modeMsg, setModeMsg] = useState('');
  const setMode = async (mode: string) => {
    if (mode === doctor.mode) return;
    if (mode === 'apply' && !window.confirm(
      `Let the Label Doctor apply its fixes?\n\nFor a FIXABLE diagnosis it will write its verified values (phone, e-mail, postcode, city, street, house number) as corrections signed "Claude API" and request one label re-buy — or just request the re-buy when the data is right and the refusal clears on retry. Each re-buy is a carrier call (a label, when it succeeds). Cap: ${doctor.apply_daily_cap ?? 20} per day. Never a pickup point, country or delivery type; never over a value a person typed. One automatic step per order — a second refusal goes to a person.\n\nYou can switch back at any time; it takes effect within 5 minutes.`)) return;
    setModeBusy(true); setModeMsg('');
    const res = await authFetch(`${API}/label-doctor/mode`, { method: 'PUT', body: JSON.stringify({ mode }) });
    const d = await res.json().catch(() => ({}));
    setModeBusy(false);
    if (!res.ok) { setModeMsg(d.detail || `Could not save (HTTP ${res.status})`); return; }
    setModeMsg(mode === 'apply' ? '✓ Apply mode on — the next cycle (≤5 min) may fix and retry.' : '✓ Diagnose-only — notes only from the next cycle.');
    load();
  };
  return (
    <div className="space-y-5 max-w-3xl">
      <p className="text-gray-500 text-sm">
        The MCP tab is Claude connecting into the WMS; this is the WMS calling Claude's API itself.
        The <span className="text-gray-300">Label Doctor</span> reads every failed label buy, checks the address and our
        pickup-point lists, and leaves one short note on the order — attributed
        <span className="text-gray-300"> Claude API</span>, so it is never mistaken for interactive work.
        {doctor.mode === 'apply'
          ? <> It is in <span className="text-purple-300">apply</span> mode: it may fix verified contact fields and request a label re-buy.</>
          : <> It is <span className="text-gray-300">diagnose-only</span>: it changes nothing.</>}
        {' '}Rules live in <code className="text-gray-400">{doctor.rules_file || 'docs/LABEL-DOCTOR-RULES.md'}</code> and apply without a deploy.
      </p>

      <div className={CARD}>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">API key</div>
        {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
        {info && (
          <div className="text-sm mb-3">
            {info.configured ? (
              <span className="text-green-400">● configured (…{info.key_tail})
                <span className="text-gray-500"> — from these settings{info.workspace_id ? `, workspace ${info.workspace_id}` : ''}{info.updated_at ? `, saved ${new Date(info.updated_at).toLocaleString()}` : ''}</span>
              </span>
            ) : (
              <span className="text-amber-400">● no key — the Label Doctor is dormant</span>
            )}
          </div>
        )}
        <div className="flex gap-2 items-center flex-wrap">
          <input type="password" value={keyInput} onChange={e => setKeyInput(e.target.value)}
            placeholder="sk-ant-…  (console.anthropic.com → API keys)"
            className="flex-1 min-w-[280px] px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500" />
          <input value={wsInput} onChange={e => setWsInput(e.target.value)}
            placeholder="Workspace ID (wrkspc_…) — identity-linked keys only"
            title="Only needed for keys created as 'works across workspaces'. Console → Settings → Workspaces → your workspace → Workspace ID."
            className="w-72 px-3 py-2 rounded bg-[#0f1117] border border-gray-700 text-white text-sm font-mono focus:outline-none focus:border-blue-500" />
          <button onClick={() => save(false)} disabled={busy || !keyInput.trim()}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg">
            {busy ? 'Verifying…' : 'Verify & save'}
          </button>
          {info?.configured && info?.source === 'settings' && (
            <button onClick={() => save(true)} disabled={busy}
              className="px-3 py-2 bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 text-sm rounded-lg">
              Remove
            </button>
          )}
        </div>
        {msg && <div className={`text-xs mt-2 ${msg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{msg}</div>}
        <p className="text-gray-600 text-xs mt-2">
          Saving makes one free verification call to the Anthropic API, so a mistyped key is refused here
          instead of failing silently overnight. Billing is per diagnosis — cents per day at the current failure volume.
        </p>
      </div>

      {info && (
        <div className={CARD}>
          <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Label Doctor</div>
          <div className="mb-4 space-y-2">
            {[
              { key: 'diagnose-only', label: 'Diagnose only', desc: 'One note per failed label buy with what is wrong and what it would change. Nothing is applied.' },
              { key: 'apply', label: 'Apply fixes & retry', desc: `For a FIXABLE diagnosis: writes verified contact values as corrections (signed Claude API) and requests one label re-buy — or only the re-buy when the data is right. Never a pickup point, country or delivery type; never over a person's value. Cap ${doctor.apply_daily_cap ?? 20}/day.` },
            ].map(m => (
              <label key={m.key} className={`flex gap-3 items-start p-3 rounded-lg border cursor-pointer ${doctor.mode === m.key ? 'border-purple-700 bg-purple-900/10' : 'border-gray-800 hover:border-gray-700'}`}>
                <input type="radio" name="ld-mode" className="mt-1 accent-purple-500" checked={doctor.mode === m.key}
                  disabled={modeBusy || !info?.configured} onChange={() => setMode(m.key)} />
                <div>
                  <div className="text-sm text-gray-200">{m.label}</div>
                  <div className="text-xs text-gray-500">{m.desc}</div>
                </div>
              </label>
            ))}
            {modeMsg && <div className={`text-xs ${modeMsg.startsWith('✓') ? 'text-green-400' : 'text-red-400'}`}>{modeMsg}</div>}
            {doctor.mode_set_by && <div className="text-xs text-gray-600">Mode set by {doctor.mode_set_by}{doctor.mode_set_at ? ` on ${new Date(doctor.mode_set_at + 'Z').toLocaleString()}` : ''}. Takes effect within 5 minutes, no restart.</div>}
          </div>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
            <div><div className="text-gray-500 text-xs mb-0.5">Applied today</div><div className="text-gray-200">{doctor.applied_today ?? 0} / {doctor.apply_daily_cap ?? 20} cap{doctor.retries_pending ? ` · ${doctor.retries_pending} re-buy pending` : ''}</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Model</div><div className="text-gray-200">{doctor.model}</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Notes today</div><div className="text-gray-200">{doctor.notes_today} / {doctor.daily_cap} cap</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Failures waiting</div><div className="text-gray-200">{doctor.failures_waiting}</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Awaiting outcome</div><div className="text-gray-200">{doctor.unresolved ?? '—'}</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Reflections today</div><div className="text-gray-200">{doctor.reflections_today ?? 0} / {doctor.reflect_daily_cap ?? 10} cap</div></div>
            <div><div className="text-gray-500 text-xs mb-0.5">Memory</div><div className="text-gray-200">{doctor.memory_files ?? 0} files, {((doctor.memory_bytes ?? 0) / 1024).toFixed(1)} / {((doctor.memory_cap ?? 32768) / 1024).toFixed(0)} KB</div></div>
          </div>
        </div>
      )}

      <DoctorDiagnoses />
      <DoctorMemory />
    </div>
  );
}

// Diagnoses next to their real-world outcomes — the calibration view. When the
// agreement column keeps saying "n/n fields", the doctor has earned write powers.
function DoctorDiagnoses() {
  const [rows, setRows] = useState<any[] | null>(null);
  useEffect(() => {
    authFetch(`${API}/label-doctor/diagnoses?limit=30`)
      .then(r => (r.ok ? r.json() : { diagnoses: [] }))
      .then(d => setRows(d.diagnoses || []));
  }, []);
  const OUTCOME: Record<string, string> = {
    fixable: 'bg-green-900/60 text-green-400', needs_human: 'bg-amber-900/50 text-amber-300',
    island_no_ship: 'bg-red-900/50 text-red-400',
  };
  const RESOLUTION: Record<string, string> = {
    label_bought: 'Label bought', moved_contact: 'Moved to Contact', cancelled: 'Cancelled',
    error_cleared: 'Error cleared', stale: 'Stale (14d)',
  };
  const agreement = (r: any) => {
    const c = r.resolution_detail?.comparison;
    if (!c) return '—';
    const ks = Object.keys(c);
    if (!ks.length) return '—';
    const hit = ks.filter(k => c[k]?.match).length;
    return `${hit}/${ks.length} fields`;
  };
  if (!rows || rows.length === 0) return null;
  return (
    <div className={CARD}>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-3">Recent diagnoses & outcomes</div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[560px]">
          <thead>
            <tr className="text-gray-500 text-xs uppercase tracking-wider border-b border-gray-800">
              <th className="py-1.5 pr-3 text-left">Order</th>
              <th className="py-1.5 pr-3 text-left">Diagnosis</th>
              <th className="py-1.5 pr-3 text-left">Applied</th>
              <th className="py-1.5 pr-3 text-left">What happened</th>
              <th className="py-1.5 pr-3 text-left">Agreement</th>
              <th className="py-1.5 text-left">Date</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(r => (
              <tr key={r.id} className="border-b border-gray-800/50 align-top" title={r.note}>
                <td className="py-1.5 pr-3 text-gray-300 whitespace-nowrap">{r.order_number || r.order_id}</td>
                <td className="py-1.5 pr-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${OUTCOME[r.outcome] || 'bg-gray-700 text-gray-300'}`}>
                    {(r.outcome || '').replace(/_/g, ' ')}
                  </span>
                </td>
                <td className="py-1.5 pr-3 whitespace-nowrap text-xs" title={r.apply_skipped ? 'Not applied: ' + Object.entries(r.apply_skipped).map(([k, v]) => `${k}: ${v}`).join('; ') : undefined}>
                  {r.applied_at
                    ? <span className="text-purple-300">{r.apply_action === 'retry_unchanged' ? 'retry' : '✓ ' + Object.keys(r.applied_changes || {}).join(', ')}</span>
                    : r.apply_skipped ? <span className="text-amber-400">skipped</span> : <span className="text-gray-600">—</span>}
                </td>
                <td className="py-1.5 pr-3 text-gray-300 whitespace-nowrap">
                  {r.resolution ? RESOLUTION[r.resolution] || r.resolution
                    : <span className="text-gray-600">waiting…</span>}
                </td>
                <td className="py-1.5 pr-3 text-gray-300 whitespace-nowrap">{r.applied_at ? <span className="text-gray-600" title="Applied by the Doctor itself — not calibration">n/a</span> : agreement(r)}</td>
                <td className="py-1.5 text-gray-500 whitespace-nowrap">{new Date(r.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-gray-600 text-xs mt-2">
        Hover a row for the full note. "Applied" is what the Doctor did itself in apply mode (hover "skipped" for why
        a value was refused). "Agreement" compares its proposed fix with the values a person actually shipped with —
        the calibration evidence; rows the Doctor applied itself are excluded from it.
      </p>
    </div>
  );
}

// The doctor's self-maintained memory: written by the model itself, capped at
// 32KB, always overridden by the rules file — and always safe to delete.
function DoctorMemory() {
  const [data, setData] = useState<any>(null);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [busy, setBusy] = useState(false);
  const load = useCallback(() => {
    authFetch(`${API}/label-doctor/memory`)
      .then(r => (r.ok ? r.json() : null)).then(setData);
  }, []);
  useEffect(() => { load(); }, [load]);
  const toggle = (p: string) => setOpen(s => { const n = new Set(s); n.has(p) ? n.delete(p) : n.add(p); return n; });
  const del = async (p: string) => {
    const label = p === '*' ? 'ALL memory files' : p;
    if (!window.confirm(`Delete ${label}? Always safe — the doctor re-learns from future outcomes.`)) return;
    setBusy(true);
    await authFetch(`${API}/label-doctor/memory?path=${encodeURIComponent(p)}`, { method: 'DELETE' });
    setBusy(false);
    load();
  };
  if (!data) return null;
  return (
    <div className={CARD}>
      <div className="flex items-center justify-between mb-3">
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold">Memory (self-maintained)</div>
        {data.files.length > 0 && (
          <button onClick={() => del('*')} disabled={busy}
            className="text-xs px-2.5 py-1 rounded bg-gray-800 hover:bg-red-900/60 text-gray-400 hover:text-red-300 disabled:opacity-50">
            Wipe all memory
          </button>
        )}
      </div>
      {data.files.length === 0 ? (
        <p className="text-gray-600 text-sm">Empty — the doctor writes its first lesson after its first
          diagnosis-vs-outcome reflection.</p>
      ) : (
        <div className="space-y-2">
          <div className="text-gray-500 text-xs">{data.files.length} file{data.files.length !== 1 ? 's' : ''},
            {' '}{(data.total_bytes / 1024).toFixed(1)} KB of {(data.cap / 1024).toFixed(0)} KB</div>
          {data.files.map((f: any) => (
            <div key={f.path} className="border border-gray-800 rounded-lg">
              <div className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-gray-800/40"
                onClick={() => toggle(f.path)}>
                <span className="text-gray-500">{open.has(f.path) ? '▾' : '▸'}</span>
                <code className="text-gray-300 text-sm flex-1">{f.path}</code>
                <span className="text-gray-600 text-xs">{f.bytes} B · {new Date(f.updated_at).toLocaleString()}</span>
                <button onClick={e => { e.stopPropagation(); del(f.path); }} disabled={busy}
                  className="text-xs px-2 py-0.5 rounded bg-gray-800 hover:bg-red-900/60 text-gray-500 hover:text-red-300">
                  Delete
                </button>
              </div>
              {open.has(f.path) && (
                <pre className="px-4 py-3 border-t border-gray-800 text-xs text-gray-300 whitespace-pre-wrap overflow-x-auto">{f.content}</pre>
              )}
            </div>
          ))}
        </div>
      )}
      <p className="text-gray-600 text-xs mt-2">
        Written by the model itself after comparing its diagnoses with real outcomes. The rules file
        always overrides it. Deleting is always safe — it re-learns.
      </p>
    </div>
  );
}

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(text); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* clipboard unavailable (http, old browser) — the URL is selectable text anyway */ }
  };
  return (
    <button onClick={copy}
      className="px-2 py-1 text-xs rounded border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition-colors">
      {copied ? 'Copied ✓' : 'Copy'}
    </button>
  );
}

function McpConnectorPanel() {
  const [info, setInfo] = useState<any>(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/settings/connectors/mcp`);
      if (!res.ok) { setError(`Failed to load (${res.status})`); return; }
      setInfo(await res.json());
      setError('');
    } catch { setError('Failed to load'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const url = info?.connector_url || 'https://wms.motleydenim.ai/mcp';
  const svc = info?.service;

  return (
    <div className="space-y-6">
      <p className="text-gray-500 text-sm">
        The MCP connector lets Claude (claude.ai, the desktop app, Claude Code) read WMS data —
        and, on your explicit ask, move an order between tabs, add an order note, register an
        exchange, and (admins and managers) mark manual refunds processed. Everyone signs in
        with their own WMS account and keeps their own role; every tool call is logged below.
      </p>

      {/* ── how to connect ─────────────────────────────────────────────── */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">How to connect</div>
        <div className={CARD}>
          <div className="flex items-center justify-between gap-3 mb-4">
            <div>
              <div className="text-white text-sm font-medium">Connector URL</div>
              <code className="text-blue-300 text-sm select-all">{url}</code>
            </div>
            <CopyButton text={url} />
          </div>
          <ol className="text-sm text-gray-400 list-decimal ml-5 space-y-1.5">
            <li><span className="text-gray-300">claude.ai / Claude desktop:</span> Settings → Connectors → <i>Add custom connector</i> → paste the URL above → Add → Connect.</li>
            <li><span className="text-gray-300">Claude Code (terminal):</span> <code className="text-gray-300 bg-gray-800 px-1.5 py-0.5 rounded select-all">claude mcp add --transport http wms {url}</code> then <code className="text-gray-300 bg-gray-800 px-1.5 py-0.5 rounded">/mcp</code> to sign in.</li>
            <li>A browser window asks for your <span className="text-gray-300">WMS username and password</span> — the same account you use here.</li>
          </ol>
          <div className="mt-3 text-xs text-amber-400/80 bg-amber-950/20 border border-amber-900/40 rounded-lg px-3 py-2">
            Claude gets your role: staff see only their own activity and hours; admins and
            managers see everyone's. Write actions (order moves, notes, exchanges; refunds are
            admin/manager only) run the same backend code as the WMS's own buttons and are
            recorded as "Claude (your username)". Database access is read-only. Deactivating a
            WMS account disconnects it here immediately.
          </div>
        </div>
      </div>

      {/* ── live status ────────────────────────────────────────────────── */}
      <div>
        <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Status</div>
        <div className={CARD}>
          {error && <div className="text-xs text-red-400 mb-2">{error}</div>}
          {!info ? <div className="text-gray-500 text-sm">Loading…</div> : (
            <>
              <div className="flex flex-wrap gap-x-8 gap-y-2 text-sm">
                <div>
                  <span className="text-gray-500">Service:</span>{' '}
                  {svc?.reachable
                    ? <span className="text-green-400">● running</span>
                    : <span className="text-red-400">● unreachable</span>}
                </div>
                <div><span className="text-gray-500">Calls today:</span> <span className="text-gray-200">{info.calls?.today ?? '—'}</span></div>
                <div><span className="text-gray-500">Last 7 days:</span> <span className="text-gray-200">{info.calls?.last_7_days ?? '—'}</span></div>
                {svc?.reachable && <>
                  <div><span className="text-gray-500">Token life:</span> <span className="text-gray-200">{svc.access_token_hours}h</span></div>
                  <div><span className="text-gray-500">Rate limit:</span> <span className="text-gray-200">{svc.rate_limit_per_min}/min per user</span></div>
                  <div><span className="text-gray-500">Write path:</span> <span className={svc.write_path_configured ? 'text-gray-200' : 'text-amber-400'}>{svc.write_path_configured ? 'configured' : 'not configured'}</span></div>
                </>}
                <div><span className="text-gray-500">Connected clients:</span> <span className="text-gray-200">{info.clients?.length ?? 0}</span></div>
              </div>
              {info.state_db_error && (
                <div className="mt-2 text-xs text-amber-400">Audit DB not readable: {info.state_db_error}</div>
              )}
              {(info.calls?.per_user_7_days || []).length > 0 && (
                <div className="mt-3 text-xs text-gray-500">
                  Last 7 days by user:{' '}
                  {info.calls.per_user_7_days.map((u: any, i: number) => (
                    <span key={u.username}>{i > 0 && ' · '}<span className="text-gray-300">{u.username}</span> {u.calls}{u.errors > 0 && <span className="text-amber-400"> ({u.errors} err)</span>}</span>
                  ))}
                </div>
              )}
              {svc?.reachable && (svc.tools || []).length > 0 && (
                <div className="mt-4">
                  <div className="text-gray-500 text-xs mb-1.5">Tools ({svc.tools.length}):</div>
                  <div className="space-y-1">
                    {svc.tools.map((t: any) => (
                      <div key={t.name} className="text-xs">
                        <code className="text-gray-300">{t.name}</code>
                        {(svc.write_tools || []).includes(t.name) && <span className="ml-1.5 text-[10px] uppercase tracking-wide text-amber-400 border border-amber-900/60 rounded px-1">write</span>}
                        <span className="text-gray-500 ml-2">{t.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* ── audit log ──────────────────────────────────────────────────── */}
      <McpAuditLog />
    </div>
  );
}

function McpAuditLog() {
  const [data, setData] = useState<any>(null);
  const [page, setPage] = useState(1);
  const [user, setUser] = useState('');
  const [tool, setTool] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ page: String(page), user, tool });
      const res = await authFetch(`${API}/settings/connectors/mcp/log?${qs}`);
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        setError(d.detail || `Failed to load (${res.status})`);
        setData(null);
        return;
      }
      setData(await res.json());
      setError('');
    } catch { setError('Failed to load'); }
  }, [page, user, tool]);
  useEffect(() => { load(); }, [load]);

  const when = (ts: string) => ts ? new Date(ts).toLocaleString('et-EE', { dateStyle: 'short', timeStyle: 'medium' }) : '';
  const sel = 'bg-gray-800 border border-gray-700 text-gray-300 text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:border-blue-500';

  return (
    <div>
      <div className="text-gray-500 text-xs uppercase tracking-widest font-semibold mb-2 px-1">Audit log</div>
      <div className={CARD}>
        <div className="flex items-center gap-2 mb-3">
          <select value={user} onChange={e => { setUser(e.target.value); setPage(1); }} className={sel}>
            <option value="">All users</option>
            {(data?.users || []).map((u: string) => <option key={u} value={u}>{u}</option>)}
          </select>
          <select value={tool} onChange={e => { setTool(e.target.value); setPage(1); }} className={sel}>
            <option value="">All tools</option>
            {(data?.tools || []).map((t: string) => <option key={t} value={t}>{t}</option>)}
          </select>
          <span className="text-gray-600 text-xs ml-auto">{data ? `${data.total} calls` : ''}</span>
        </div>

        {error && <div className="text-xs text-amber-400 mb-2">{error}</div>}
        {data && data.entries.length === 0 && (
          <div className="text-gray-600 text-sm py-6 text-center border border-dashed border-gray-800 rounded-lg">No tool calls logged{user || tool ? ' for this filter' : ' yet'}.</div>
        )}
        {data && data.entries.length > 0 && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-gray-500 text-left">
                <tr>
                  <th className="py-1.5 pr-3 font-medium">When</th>
                  <th className="py-1.5 pr-3 font-medium">Who</th>
                  <th className="py-1.5 pr-3 font-medium">Tool</th>
                  <th className="py-1.5 pr-3 font-medium">Arguments</th>
                  <th className="py-1.5 pr-3 font-medium text-right">Rows</th>
                  <th className="py-1.5 font-medium">Error</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e: any) => (
                  <tr key={e.id} className="border-t border-gray-800/60">
                    <td className="py-1.5 pr-3 text-gray-400 whitespace-nowrap">{when(e.ts)}</td>
                    <td className="py-1.5 pr-3 text-gray-300">{e.username || '—'}{e.via && String(e.via).startsWith('api:') && <span className="ml-1 text-[10px] px-1.5 py-0.5 rounded bg-sky-900/60 text-sky-200" title="Called over the REST API with this key">REST · {String(e.via).slice(4)}</span>}</td>
                    <td className="py-1.5 pr-3"><code className="text-gray-300">{e.tool}</code></td>
                    <td className="py-1.5 pr-3 text-gray-500 max-w-[26rem] truncate" title={e.arguments || ''}>{e.arguments}</td>
                    <td className="py-1.5 pr-3 text-gray-400 text-right">{e.rows ?? ''}</td>
                    <td className="py-1.5 text-amber-400 max-w-[16rem] truncate" title={e.error || ''}>{e.error || ''}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {data && data.pages > 1 && (
          <div className="flex items-center justify-between mt-3 text-xs text-gray-500">
            <button disabled={page <= 1} onClick={() => setPage(p => p - 1)}
              className="px-3 py-1 rounded border border-gray-700 disabled:opacity-30 hover:text-white transition-colors">Previous</button>
            <span>Page {data.page} of {data.pages}</span>
            <button disabled={page >= data.pages} onClick={() => setPage(p => p + 1)}
              className="px-3 py-1 rounded border border-gray-700 disabled:opacity-30 hover:text-white transition-colors">Next</button>
          </div>
        )}
      </div>
    </div>
  );
}
